import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { certificatesApi } from '../services/api';
import { copyText } from '@/utils/clipboard';
import { toast } from '@/lib/toast';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { MAX_VALIDITY_HOURS, validateIssueForm, type IssueFormValues } from '@/lib/certIssue';
import { isExpiringSoon, isRevoked, sortByExpiry, type Certificate, type ExpirySort } from '@/lib/certExpiry';
import {
    Shield,
    Key,
    Lock,
    Plus,
    Search,
    Download,
    Trash2,
    CheckCircle,
    XCircle,
    Clock,
    AlertTriangle,
    Copy,
    ShieldCheck,
    SearchX,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Button, Card, CardButton, Chip, EmptyState, IconButton, Input, ListPageSkeleton, Modal, MonoCell, OutcomeBadge, PageHeader, SearchField, Select, StatCard, Textarea, Timestamp } from '@/components/ds-zta';
import StatValue from '@/components/StatValue';

/** Saves `content` as a local file via a Blob URL. */
function downloadText(content: string, filename: string) {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

const certFilename = (c: Pick<Certificate, 'certificate_id' | 'type'>) =>
    c.type === 'ssh' ? `${c.certificate_id}-cert.pub` : `${c.certificate_id}.pem`;

const errorMessage = (err: unknown, fallback: string) => {
    const e = err as { response?: { data?: { error?: string } }; message?: string };
    return e?.response?.data?.error || e?.message || fallback;
};

/* ─── Certificates Page ─────────────────────────────── */
export default function Certificates() {
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<'all' | 'ssh' | 'tls'>('all');
    const [expirySort, setExpirySort] = useState<ExpirySort>('default');
    const [showIssueModal, setShowIssueModal] = useState(false);
    const [copied, setCopied] = useState('');
    const { confirm } = useDialogs();

    const { data: certsData, refetch, isFetching, isLoading, isError } = useQuery(
        'certificates',
        () => certificatesApi.list(),
        { retry: 1, refetchOnWindowFocus: false }
    );

    const certificates: Certificate[] = certsData?.data || [];

    const filteredCerts = sortByExpiry(certificates.filter((cert) => {
        const matchesSearch =
            cert.certificate_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cert.fingerprint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cert.metadata?.subject?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' || cert.type === typeFilter;
        return matchesSearch && matchesType;
    }), expirySort);

    const sshCerts = certificates.filter((c) => c.type === 'ssh');
    const tlsCerts = certificates.filter((c) => c.type === 'tls');
    const isExpired = (expires: string) => new Date(expires) <= new Date();
    const activeCerts = certificates.filter((c) => !isRevoked(c) && !isExpired(c.expires));
    const expiringCerts = activeCerts.filter((c) => isExpiringSoon(c));
    const expiredOrRevokedCerts = certificates.filter((c) => isRevoked(c) || isExpired(c.expires));

    const copyToClipboard = async (text: string, id: string) => {
        if (!(await copyText(text))) {
            toast.error('Could not copy to the clipboard');
            return;
        }
        setCopied(id);
        setTimeout(() => setCopied(''), 2000);
    };

    const certLabel = (c: Certificate) => c.metadata?.subject || c.certificate_id.slice(0, 12);

    const revokeMutation = useMutation(
        (cert: Certificate) => certificatesApi.revoke(cert.certificate_id),
        {
            onSuccess: (_res, cert) => {
                toast.success(`Certificate "${certLabel(cert)}" revoked`);
                refetch();
            },
            onError: (err, cert) => { toast.error(`Failed to revoke "${certLabel(cert)}": ${errorMessage(err, 'unknown error')}`); },
        }
    );

    const handleRevoke = async (cert: Certificate) => {
        if (await confirm({
            title: 'Revoke certificate?',
            message: `Revoke ${cert.type.toUpperCase()} certificate "${certLabel(cert)}" (${cert.certificate_id})? Anything using it stops authenticating. This cannot be undone.`,
            confirmLabel: 'Revoke',
            variant: 'danger',
        })) {
            revokeMutation.mutate(cert);
        }
    };

    return (
        <div className="animate-fade-in space-y-6">
            {/* Header */}
            <PageHeader
                icon={<ShieldCheck />}
                title="Certificates"
                description="Manage SSH and TLS certificates issued by the internal CA"
                primaryAction={
                    <Button onClick={() => setShowIssueModal(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                        Issue Certificate
                    </Button>
                }
                onRefresh={() => refetch()}
                refreshing={isFetching}
            />

            {/* Stat Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard icon={<Key />} label="SSH Certificates" value={sshCerts.length} />
                <StatCard icon={<Lock />} label="TLS Certificates" value={tlsCerts.length} />
                <StatCard icon={<CheckCircle />} label="Active" accent="success" value={<StatValue value={activeCerts.length} caption={expiringCerts.length > 0 ? `${expiringCerts.length} expiring soon` : undefined} />} />
                <StatCard icon={<XCircle />} label="Expired / Revoked" value={expiredOrRevokedCerts.length} />
            </div>

            {/* Search + Filters */}
            <Card className="p-4 overflow-visible">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                        <SearchField
                          label="Search by ID, fingerprint, or subject"
                          placeholder="Search by ID, fingerprint, or subject..."
                          value={searchTerm}
                          onChange={(value) => setSearchTerm(value)}
                        />
                    </div>
                    {/* Selects, not rows of 27px chips: every control in this
                        row is the 40px md size (design eval F46). */}
                    <div className="sm:w-40">
                        <Select
                            className="gap-0"
                            label={<span className="sr-only">Certificate type</span>}
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value as 'all' | 'ssh' | 'tls')}
                        >
                            <option value="all">All Types</option>
                            <option value="ssh">SSH</option>
                            <option value="tls">TLS</option>
                        </Select>
                    </div>
                    <div className="sm:w-48">
                        <Select
                            className="gap-0"
                            label={<span className="sr-only">Sort by expiry</span>}
                            value={expirySort}
                            onChange={(e) => setExpirySort(e.target.value as ExpirySort)}
                        >
                            <option value="default">Default order</option>
                            <option value="soonest">Expires soonest</option>
                            <option value="latest">Expires latest</option>
                        </Select>
                    </div>
                </div>
            </Card>

            {/* Certificates List */}
            <Card className="overflow-hidden">
                {isLoading ? (
                    <ListPageSkeleton className="p-5" filterCount={0} rowCount={6} />
                ) : isError ? (
                    <EmptyState
                        icon={<AlertTriangle />}
                        title="Failed to load certificates"
                        description="Something went wrong while loading certificates."
                        action={<Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>}
                    />
                ) : filteredCerts.length === 0 ? (
                    certificates.length === 0 ? (
                        <EmptyState
                            icon={<Shield />}
                            title="No certificates yet"
                            description="Issue your first certificate to get started."
                            action={
                                <Button size="sm" onClick={() => setShowIssueModal(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                                    Issue Certificate
                                </Button>
                            }
                        />
                    ) : (
                        <EmptyState
                            icon={<SearchX />}
                            title={searchTerm ? `No certificates match "${searchTerm}"` : 'No certificates match this filter'}
                            description="Try adjusting your search or filter."
                        />
                    )
                ) : (
                    <div className="divide-y divide-[var(--d2d-border)]">
                        {filteredCerts.map((cert) => (
                            <div
                                key={cert.certificate_id}
                                className="flex items-center justify-between px-5 py-4 hover:bg-[var(--ds-color-bg-hover)] transition-colors group"
                            >
                                <div className="flex items-center gap-4 min-w-0">
                                    <div
                                        className={clsx(
                                            'flex items-center justify-center h-10 w-10 rounded-xl flex-shrink-0',
                                            cert.type === 'ssh'
                                                ? 'bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)]'
                                                : 'bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)]'
                                        )}
                                    >
                                        {cert.type === 'ssh' ? (
                                            <Key className="h-5 w-5" />
                                        ) : (
                                            <Lock className="h-5 w-5" />
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-[var(--d2d-text-primary)] truncate">
                                                {certLabel(cert)}
                                            </span>
                                            <Chip variant="neutral" className="uppercase">
                                                {cert.type}
                                            </Chip>
                                            {isRevoked(cert) ? (
                                                <OutcomeBadge value="revoked" shape="chip">Revoked</OutcomeBadge>
                                            ) : isExpired(cert.expires) ? (
                                                <OutcomeBadge value="certificate_expired" shape="chip">Expired</OutcomeBadge>
                                            ) : isExpiringSoon(cert) ? (
                                                <OutcomeBadge value="expiring" shape="chip">Expiring soon</OutcomeBadge>
                                            ) : (
                                                <OutcomeBadge value="valid" shape="chip">Active</OutcomeBadge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-[var(--d2d-text-muted)]">
                                            {cert.fingerprint && <MonoCell value={cert.fingerprint} width={200} />}
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                Expires{' '}
                                                <Timestamp value={cert.expires} noSeconds fallback={cert.expires || '—'} />
                                            </span>
                                            {cert.metadata?.principals && cert.metadata.principals.length > 0 && (
                                                <span>
                                                    Principals: {cert.metadata.principals.join(', ')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-end gap-1 flex-shrink-0">
                                    <IconButton
                                        size="sm"
                                        aria-label="Copy certificate"
                                        tooltip="Copy certificate"
                                        onClick={() => copyToClipboard(cert.certificate, cert.certificate_id)}
                                        icon={
                                            copied === cert.certificate_id ? (
                                                <CheckCircle className="h-4 w-4 text-[var(--d2d-success)]" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )
                                        }
                                    />
                                    <IconButton
                                        size="sm"
                                        aria-label="Download certificate"
                                        tooltip="Download certificate"
                                        onClick={() => downloadText(cert.certificate, certFilename(cert))}
                                        icon={<Download className="h-4 w-4" />}
                                    />
                                    {!isExpired(cert.expires) && !isRevoked(cert) && (
                                        <IconButton
                                            size="sm"
                                            aria-label={`Revoke certificate ${certLabel(cert)}`}
                                            tooltip="Revoke certificate"
                                            className="hover:text-[var(--danger-text)]"
                                            onClick={() => handleRevoke(cert)}
                                            disabled={revokeMutation.isLoading && revokeMutation.variables?.certificate_id === cert.certificate_id}
                                            icon={<Trash2 className="h-4 w-4" />}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Issue Certificate Modal */}
            <IssueCertModal
                isOpen={showIssueModal}
                onClose={() => setShowIssueModal(false)}
                onIssued={() => {
                    refetch();
                    setShowIssueModal(false);
                }}
            />
        </div>
    );
}

/* ─── Issue Certificate Modal ───────────────────────── */
function IssueCertModal({
    isOpen,
    onClose,
    onIssued,
}: {
    isOpen: boolean;
    onClose: () => void;
    onIssued: () => void;
}) {
    const [certType, setCertType] = useState<'ssh' | 'tls'>('ssh');
    const [commonName, setCommonName] = useState('');
    const [validity, setValidity] = useState('8');
    const [principals, setPrincipals] = useState('');
    const [issuing, setIssuing] = useState(false);
    const [result, setResult] = useState<Certificate | null>(null);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof IssueFormValues, string>>>({});

    const handleIssue = async () => {
        setError('');
        // F12: no silent defaults. Blank principals / CN and out-of-range validity
        // are field errors instead of an `admin` / `localhost` certificate.
        const { request, errors } = validateIssueForm(certType, { principals, commonName, validity });
        setFieldErrors(errors);
        if (!request) return;
        setIssuing(true);
        try {
            const res = request.type === 'ssh'
                ? await certificatesApi.issueSSH(request.body)
                : await certificatesApi.issueTLS(request.body);
            setResult(res.data);
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { error?: string; details?: string }; status?: number }; message?: string };
            const msg = axiosErr.response?.data?.error
                ? `${axiosErr.response.data.error}${axiosErr.response.data.details ? ': ' + axiosErr.response.data.details : ''}`
                : axiosErr.message || 'Failed to issue certificate';
            setError(`${msg} (${axiosErr.response?.status || 'network error'})`);
        } finally {
            setIssuing(false);
        }
    };

    const handleClose = () => {
        if (result) onIssued();
        else onClose();
        setResult(null);
        setError('');
        setFieldErrors({});
    };

    // F12: the private key is shown exactly once. While it is on screen the modal
    // ignores backdrop clicks, Escape and the X; only the explicit "I've saved the
    // key" button closes it.
    const holdingKey = !!result?.private_key;

    return (
        <Modal
            open={isOpen}
            onClose={holdingKey ? () => {} : handleClose}
            dismissOnOverlay={false}
            showCloseButton={!holdingKey}
            size="md"
            title="Issue Certificate"
            footer={
                <>
                    <Button variant={holdingKey ? 'primary' : 'secondary'} onClick={handleClose}>
                        {holdingKey ? "I've saved the key" : result ? 'Done' : 'Cancel'}
                    </Button>
                    {!result && (
                        <Button onClick={handleIssue} disabled={issuing} loading={issuing}>
                            {issuing ? 'Issuing…' : 'Issue Certificate'}
                        </Button>
                    )}
                </>
            }
        >
            <div className="space-y-5">
                    {result ? (
                        // Success view
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-4 bg-[var(--zta-tint-success)] rounded-xl border border-[var(--zta-tint-success-border)]">
                                <CheckCircle className="h-5 w-5 text-[var(--d2d-success)] flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-[var(--d2d-success)]">
                                        Certificate issued successfully
                                    </p>
                                    <p className="flex items-center gap-1 text-xs text-[var(--d2d-success)]">
                                        ID: <MonoCell value={result.certificate_id} />
                                    </p>
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span aria-hidden="true" className="block text-label font-semibold text-[var(--d2d-text-muted)] uppercase">
                                        Certificate
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        leadingIcon={<Download className="h-3 w-3" />}
                                        onClick={() => downloadText(result.certificate, certFilename(result))}
                                    >
                                        Download
                                    </Button>
                                </div>
                                <Textarea
                                    label={<span className="sr-only">Certificate</span>}
                                    readOnly
                                    value={result.certificate}
                                    rows={4}
                                    className="[&_textarea]:font-mono [&_textarea]:text-micro"
                                />
                            </div>
                            {result.private_key && (
                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center gap-2">
                                            <span aria-hidden="true" className="block text-label font-semibold text-[var(--d2d-text-muted)] uppercase">
                                                Private Key
                                            </span>
                                            <Chip variant="danger" icon={<AlertTriangle className="h-3 w-3" />}>
                                                Secret
                                            </Chip>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            leadingIcon={<Download className="h-3 w-3" />}
                                            onClick={() => downloadText(result.private_key!, `${result.certificate_id}-key`)}
                                        >
                                            Download
                                        </Button>
                                    </div>
                                    <Textarea
                                        label={<span className="sr-only">Private Key</span>}
                                        readOnly
                                        value={result.private_key}
                                        rows={4}
                                        className="[&_textarea]:font-mono [&_textarea]:text-micro"
                                    />
                                </div>
                            )}

                            {/* Download All button */}
                            <div className="flex gap-2 pt-1">
                                <Button
                                    className="w-full"
                                    leadingIcon={<Download className="h-4 w-4" />}
                                    onClick={() => {
                                        // Download both cert and key as separate files
                                        downloadText(result.certificate, certFilename(result));
                                        if (result.private_key) {
                                            setTimeout(() => downloadText(result.private_key!, `${result.certificate_id}-key`), 300);
                                        }
                                    }}
                                >
                                    Download All Files
                                </Button>
                            </div>
                        </div>
                    ) : (
                        // Issue form
                        <>
                            {/* Type selector */}
                            <div>
                                <label className="block text-label font-semibold text-[var(--d2d-text-muted)] mb-2 uppercase">
                                    Certificate Type
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    {(['ssh', 'tls'] as const).map((t) => (
                                        <CardButton
                                            key={t}
                                            aria-pressed={certType === t}
                                            onClick={() => { setCertType(t); setFieldErrors({}); }}
                                            className={clsx(
                                                'flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all',
                                                certType === t
                                                    ? 'border-[var(--d2d-text-primary)] bg-[var(--ds-color-bg-subtle)]'
                                                    : 'border-[var(--d2d-border)] hover:border-[var(--ds-color-border-strong)]'
                                            )}
                                        >
                                            {t === 'ssh' ? (
                                                <Key className={clsx('h-5 w-5', certType === t ? 'text-[var(--d2d-text-primary)]' : 'text-[var(--d2d-text-muted)]')} />
                                            ) : (
                                                <Lock className={clsx('h-5 w-5', certType === t ? 'text-[var(--d2d-text-primary)]' : 'text-[var(--d2d-text-muted)]')} />
                                            )}
                                            <div className="text-left">
                                                <p className="text-sm font-medium text-[var(--d2d-text-primary)]">
                                                    {t.toUpperCase()}
                                                </p>
                                                <p className="text-micro text-[var(--d2d-text-muted)]">
                                                    {t === 'ssh' ? 'Server & user auth' : 'Service identity'}
                                                </p>
                                            </div>
                                        </CardButton>
                                    ))}
                                </div>
                            </div>

                            {/* Common Name (TLS) or Principals (SSH) */}
                            {certType === 'tls' ? (
                                <Input
                                    label="Common Name"
                                    type="text"
                                    value={commonName}
                                    onChange={(e) => setCommonName(e.target.value)}
                                    placeholder="e.g. api.company.com"
                                    required
                                    error={fieldErrors.commonName}
                                />
                            ) : (
                                <Input
                                    label="Principals"
                                    type="text"
                                    value={principals}
                                    onChange={(e) => setPrincipals(e.target.value)}
                                    placeholder="e.g. admin, deploy (comma-separated)"
                                    required
                                    error={fieldErrors.principals}
                                />
                            )}

                            {/* Validity */}
                            <div>
                                <Input
                                    label="Validity (hours)"
                                    type="number"
                                    value={validity}
                                    onChange={(e) => setValidity(e.target.value)}
                                    min="1"
                                    max={MAX_VALIDITY_HOURS[certType]}
                                    error={fieldErrors.validity}
                                />
                                <p className="text-micro text-[var(--d2d-text-muted)] mt-1">
                                    Maximum: {MAX_VALIDITY_HOURS[certType]} hours
                                </p>
                            </div>

                            {error && (
                                <div className="flex items-center gap-2 p-3 bg-[var(--zta-tint-danger)] rounded-xl border border-[var(--zta-tint-danger-border)]">
                                    <AlertTriangle className="h-4 w-4 text-[var(--danger-text)] flex-shrink-0" />
                                    <p className="text-sm text-[var(--danger-text)]">{error}</p>
                                </div>
                            )}
                        </>
                    )}
            </div>
        </Modal>
    );
}
